import { updateDoc, doc, serverTimestamp } from "firebase/firestore";
import { updateProfile, getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { app } from "../../firebase-config.js";
import { convertToWebP } from "../../utils/image-processor.js";
import { uploadImage } from "../../services/storage-service.js";
import { locale as LocaleService } from "../../services/LocaleService.js";

const db = getFirestore(app);
const auth = getAuth(app);

export class ProfileEditModal {
    constructor(user, onClose, onUpdate) {
        this.user = user;
        this.onClose = onClose;
        this.onUpdate = onUpdate;
        this.wrapper = null;
    }

    render() {
        // Prevent duplicate modals
        if (document.getElementById('profile-modal')) return;

        const modalHtml = `
            <div id="profile-modal" class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                <div class="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl scale-95 opacity-0 transition-all duration-300 flex flex-col max-h-[90vh]" id="profile-modal-box">
                    <div class="p-5 border-b border-gray-100 flex justify-between items-center">
                        <h3 class="text-lg font-bold text-gray-900">${LocaleService.t('mypage.edit_profile_title')}</h3>
                        <button id="btn-close-modal" class="text-gray-400 hover:text-gray-600"><span class="material-symbols-outlined">close</span></button>
                    </div>
                    
                    <div class="p-6 overflow-y-auto space-y-6">
                        <!-- 1. Photo Section -->
                        <div class="space-y-3">
                            <label class="block text-xs font-bold text-gray-700 uppercase">${LocaleService.t('mypage.label_photo')}</label>
                            
                            <div class="flex flex-col items-center gap-4">
                                <div class="w-24 h-24 rounded-full overflow-hidden bg-gray-100 border-2 border-gray-200">
                                    <img id="preview-photo" src="${this.user.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${this.user.uid}`}" class="w-full h-full object-cover">
                                </div>
                                <div class="flex gap-2">
                                    <label class="cursor-pointer px-3 py-1.5 bg-indigo-50 text-indigo-600 text-xs font-bold rounded-lg hover:bg-indigo-100 transition-colors">
                                        ${LocaleService.t('mypage.btn_upload')}
                                        <input type="file" id="input-file" class="hidden" accept="image/*">
                                    </label>
                                    <button type="button" id="btn-random-avatar" class="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-bold rounded-lg hover:bg-gray-200 transition-colors">${LocaleService.t('mypage.btn_random')}</button>
                                </div>
                                <div class="w-full">
                                    <input type="text" id="input-photo-url" class="w-full text-xs border border-gray-300 rounded-lg p-2 text-gray-500" placeholder="${LocaleService.t('mypage.placeholder_photo_url')}" value="${this.user.photoURL || ''}">
                                </div>
                            </div>
                        </div>

                        <!-- 2. Info Section -->
                        <div class="space-y-4">
                            <div>
                                <label class="block text-xs font-bold text-gray-700 uppercase mb-1">${LocaleService.t('mypage.label_nickname')}</label>
                                <input type="text" id="input-nickname" class="w-full border border-gray-300 rounded-xl p-3 text-sm focus:border-indigo-500 outline-none" value="${this.user.displayName || ''}">
                            </div>
                            <div>
                                <label class="block text-xs font-bold text-gray-700 uppercase mb-1">${LocaleService.t('mypage.label_bio')}</label>
                                <textarea id="input-bio" rows="2" class="w-full border border-gray-300 rounded-xl p-3 text-sm focus:border-indigo-500 outline-none resize-none" placeholder="${LocaleService.t('mypage.placeholder_bio')}">${this.user.bio || ''}</textarea>
                            </div>
                        </div>
                    </div>

                    <div class="p-5 border-t border-gray-100">
                        <button id="btn-save-profile" class="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-lg transition-transform active:scale-[0.98]">
                            ${LocaleService.t('common.save')}
                        </button>
                    </div>
                </div>
            </div>
        `;

        this.wrapper = document.createElement('div');
        this.wrapper.innerHTML = modalHtml;
        document.body.appendChild(this.wrapper);

        // Bind Events
        this._bindEvents();
        this._show();
    }

    _show() {
        const box = this.wrapper.querySelector('#profile-modal-box');
        requestAnimationFrame(() => {
            box.classList.remove('scale-95', 'opacity-0');
            box.classList.add('scale-100', 'opacity-100');
        });
    }

    _close() {
        const box = this.wrapper.querySelector('#profile-modal-box');
        box.classList.remove('scale-100', 'opacity-100');
        box.classList.add('scale-95', 'opacity-0');
        setTimeout(() => {
            this.wrapper.remove();
            if (this.onClose) this.onClose();
        }, 200);
    }

    _bindEvents() {
        const wrapper = this.wrapper;
        const box = wrapper.querySelector('#profile-modal-box');

        // Stop Propagation on box click
        box.onclick = (e) => e.stopPropagation();

        // Close on background click
        // Close on background click (Robust Logic)
        let isMouseDownOnBackdrop = false;

        wrapper.onmousedown = (e) => {
            isMouseDownOnBackdrop = e.target === wrapper.querySelector('#profile-modal');
        };

        wrapper.querySelector('#profile-modal').onclick = (e) => {
            if (e.target === wrapper.querySelector('#profile-modal') && isMouseDownOnBackdrop) {
                this._close();
            }
        };

        // Close Button
        wrapper.querySelector('#btn-close-modal').onclick = () => this._close();

        // Inputs
        const preview = wrapper.querySelector('#preview-photo');
        const urlInput = wrapper.querySelector('#input-photo-url');
        const fileInput = wrapper.querySelector('#input-file');
        const nicknameInput = wrapper.querySelector('#input-nickname');
        const bioInput = wrapper.querySelector('#input-bio');
        const saveBtn = wrapper.querySelector('#btn-save-profile');

        // Random Avatar
        wrapper.querySelector('#btn-random-avatar').onclick = () => {
            const seed = Math.random().toString(36).substring(7);
            const url = `https://api.dicebear.com/7.x/avataaars/svg?seed=${seed}`;
            urlInput.value = url;
            preview.src = url;
            fileInput.value = '';
        };

        // URL Input Sync
        urlInput.oninput = () => {
            if (urlInput.value) preview.src = urlInput.value;
        };

        // File Input Sync
        fileInput.onchange = (e) => {
            const f = e.target.files[0];
            if (f) {
                preview.src = URL.createObjectURL(f);
                urlInput.value = '';
            }
        };

        // Save Action
        saveBtn.onclick = async () => {
            const nickname = nicknameInput.value.trim();
            const bio = bioInput.value.trim();

            saveBtn.textContent = LocaleService.t('login.msg_processing');
            saveBtn.disabled = true;

            try {
                let finalPhotoURL = this.user.photoURL;

                // Handle Image Upload
                if (fileInput.files.length > 0) {
                    const compressedBlob = await convertToWebP(fileInput.files[0], { maxWidth: 500 });
                    finalPhotoURL = await uploadImage(compressedBlob, `users/${this.user.uid}/profile_${Date.now()}.webp`);
                } else if (urlInput.value && urlInput.value !== this.user.photoURL) {
                    finalPhotoURL = urlInput.value;
                }

                // Firestore Update
                await updateDoc(doc(db, "users", this.user.uid), {
                    displayName: nickname,
                    bio: bio,
                    photoURL: finalPhotoURL,
                    updatedAt: serverTimestamp()
                });

                // Auth Update
                if (auth.currentUser) {
                    await updateProfile(auth.currentUser, {
                        displayName: nickname,
                        photoURL: finalPhotoURL
                    });
                }

                // Trigger Success Callback
                if (this.onUpdate) {
                    this.onUpdate({ nickname, bio, photoURL: finalPhotoURL });
                }

                // [FIX] SPA Update (No Reload) to prevent "Login Prompt" race condition
                this._close();

                // [FIX] Update Local & Global State IMMEDIATELY
                this.user.displayName = nickname;
                this.user.bio = bio;
                this.user.photoURL = finalPhotoURL;

                if (window.user) {
                    Object.assign(window.user, { displayName: nickname, bio, photoURL: finalPhotoURL });
                }

                // [FIX] Dispatch Global Update Event with FRESH data
                window.dispatchEvent(new CustomEvent('user-profile-update', {
                    detail: { user: this.user }
                }));

                // Optional: Show Success Message
                if (window.Modal && window.Modal.toast) {
                    window.Modal.toast(LocaleService.t('common.saved') || "저장되었습니다.");
                } else {
                    // Fallback
                    // alert("저장되었습니다.");
                }
            } catch (e) {
                console.error("Profile Save Error:", e);
                alert(LocaleService.t('common.error') + ": " + e.message);
                saveBtn.textContent = LocaleService.t('common.save');
                saveBtn.disabled = false;
            }
        };
    }
}
